import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import apiService from '../services/api';

const PropertyDetail = () => {
  const { id, appId } = useParams();
  const navigate = useNavigate();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    fetchProperty();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, appId]);

  const onImgError = (e) => {
    e.target.onerror = null;
    e.target.src = 'https://placehold.co/800x600/cccccc/969696.png?text=No+Image';
  };

  const identify = async () => {
    try {
      let externalId;
      let name, username;

      // Если внутри Telegram — всегда берем его профиль
      if (window.Telegram?.WebApp?.initDataUnsafe?.user) {
        const u = window.Telegram.WebApp.initDataUnsafe.user;
        externalId = `tg_${u.id}`;
        name = [u.first_name, u.last_name].filter(Boolean).join(' ');
        username = u.username;
      } else {
        // Иначе — используем сохраненный или анонимный
        externalId = localStorage.getItem('extUserId');
        if (!externalId) {
          externalId = `anon_${Math.random().toString(36).slice(2)}`;
        }
      }
      localStorage.setItem('extUserId', externalId);
      await apiService.identifyUser({ externalId, name, username, appId });
      return externalId;
    } catch (e) {
      console.warn('identify failed', e);
      return null;
    }
  };

  const fetchProperty = async () => {
    try {
      setLoading(true);
      const data = await apiService.fetchProperty(id);
      setProperty(data);
      setError(null);
      const extId = await identify();
      try {
        await apiService.logPropertyView({ propertyId: id, externalUserId: extId });
      } catch (e) {
        console.warn('log view failed', e);
      }
    } catch (err) {
      setError('Ошибка загрузки объекта недвижимости');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleContactPhone = () => {
    if (window.Telegram?.WebApp) {
      const tg = window.Telegram.WebApp;
      tg.openLink(`tel:${property.contact.phone}`);
    } else {
      window.open(`tel:${property.contact.phone}`, '_blank');
    }
  };

  const handleContactEmail = () => {
    if (window.Telegram?.WebApp) {
      const tg = window.Telegram.WebApp;
      tg.openLink(`mailto:${property.contact.email}`);
    } else {
      window.open(`mailto:${property.contact.email}`, '_blank');
    }
  };

  const handleShowOnMap = () => {
    if (property.coordinates) {
      const mapUrl = `https://maps.google.com/?q=${property.coordinates.lat},${property.coordinates.lng}`;
      if (window.Telegram?.WebApp) {
        const tg = window.Telegram.WebApp;
        tg.openLink(mapUrl);
      } else {
        window.open(mapUrl, '_blank');
      }
    }
  };

  const nextImage = () => {
    setCurrentImageIndex((prev) => 
      prev === property.images.length - 1 ? 0 : prev + 1
    );
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => 
      prev === 0 ? property.images.length - 1 : prev - 1
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error || !property) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error || 'Объект не найден'}</p>
          <button 
            onClick={() => navigate(appId ? `/${appId}/` : '/')}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
          >
            Вернуться к каталогу
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header with back button */}
      <div className="bg-white shadow-sm sticky top-0 z-10">
        <div className="container mx-auto px-4 py-3">
          <button 
            onClick={() => navigate(appId ? `/${appId}/` : '/')}
            className="flex items-center text-blue-600 hover:text-blue-700"
          >
            <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
            </svg>
            Назад к каталогу
          </button>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6">
        {/* Image Gallery */}
        <div className="relative mb-6">
          <div className="relative h-64 md:h-96 rounded-lg overflow-hidden">
            <img 
              src={property.images && property.images[currentImageIndex] ? property.images[currentImageIndex] : 'https://placehold.co/800x600/cccccc/969696.png?text=No+Image'} 
              alt={`${property.title} - фото ${currentImageIndex + 1}`}
              className="w-full h-full object-cover"
              loading="lazy"
              onError={onImgError}
            />
            
            {property.images.length > 1 && (
              <>
                <button 
                  onClick={prevImage}
                  className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-70"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </button>
                <button 
                  onClick={nextImage}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-70"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                  </svg>
                </button>
              </>
            )}

            <div className="absolute bottom-2 left-2 bg-blue-600 text-white px-2 py-1 rounded text-sm font-medium">
              {apiService.getPropertyTypeLabel(property.type)}
            </div>
            
            {property.images.length > 1 && (
              <div className="absolute bottom-2 right-2 bg-black bg-opacity-50 text-white px-2 py-1 rounded text-sm">
                {currentImageIndex + 1} / {property.images.length}
              </div>
            )}
          </div>

          {/* Image thumbnails */}
          {property.images.length > 1 && (
            <div className="flex gap-2 mt-3 overflow-x-auto">
              {property.images.map((image, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentImageIndex(index)}
                  className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 ${
                    index === currentImageIndex ? 'border-blue-600' : 'border-gray-300'
                  }`}
                >
                  <img src={image || 'https://placehold.co/160x160/cccccc/969696.png?text=No+Image'} alt={`Thumbnail ${index + 1}`} className="w-full h-full object-cover" loading="lazy" onError={onImgError} />
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
              <h1 className="text-2xl font-bold text-gray-900 mb-4">
                {property.title}
              </h1>

              <div className="flex items-center text-gray-600 mb-4">
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                </svg>
                <span>{property.address}</span>
              </div>

              <div className="text-3xl font-bold text-blue-600 mb-6">
                {apiService.formatPrice(property.price, property.currency)}
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="text-center p-3 bg-gray-50 rounded-lg">
                  <div className="text-2xl font-bold text-gray-900">{property.area}</div>
                  <div className="text-sm text-gray-600">м²</div>
                </div>
                <div className="text-center p-3 bg-gray-50 rounded-lg">
                  <div className="text-2xl font-bold text-gray-900">{property.rooms}</div>
                  <div className="text-sm text-gray-600">комнат</div>
                </div>
                {property.floor && (
                  <div className="text-center p-3 bg-gray-50 rounded-lg">
                    <div className="text-2xl font-bold text-gray-900">{property.floor}</div>
                    <div className="text-sm text-gray-600">этаж</div>
                  </div>
                )}
                {property.land_area && (
                  <div className="text-center p-3 bg-gray-50 rounded-lg">
                    <div className="text-2xl font-bold text-gray-900">{property.land_area}</div>
                    <div className="text-sm text-gray-600">м² участок</div>
                  </div>
                )}
              </div>

              <div className="mb-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-3">Описание</h2>
                <p className="text-gray-700 leading-relaxed">{property.description}</p>
              </div>

              <div className="mb-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-3">Особенности</h2>
                <div className="flex flex-wrap gap-2">
                  {property.features.map((feature, index) => (
                    <span 
                      key={index}
                      className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm"
                    >
                      {feature}
                    </span>
                  ))}
                </div>
              </div>

              <button 
                onClick={handleShowOnMap}
                className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 py-3 px-4 rounded-lg transition-colors flex items-center justify-center"
              >
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                </svg>
                Показать на карте
              </button>
            </div>
          </div>

          {/* Sidebar - Contact Info */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-sm p-6 sticky top-24">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Контакты</h2>
              
              {property.contact && (
                <div className="mb-4">
                  <div className="font-medium text-gray-900 mb-1">{property.contact.name}</div>
                  <div className="text-gray-600 text-sm mb-3">Агент по недвижимости</div>
                  
                  <div className="space-y-3">
                    {property.contact.phone && (
                      <button 
                        onClick={handleContactPhone}
                        className="bg-green-500 hover:bg-green-600 text-white py-3 px-4 rounded-lg transition-colors flex items-center justify-center"
                      >
                        <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                        </svg>
                        {property.contact.phone}
                      </button>
                    )}
                    {property.contact.email && (
                      <button 
                        onClick={handleContactEmail}
                        className="w-full bg-blue-500 hover:bg-blue-600 text-white py-3 px-4 rounded-lg transition-colors flex items-center justify-center"
                      >
                        <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                          <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                        </svg>
                        Написать
                      </button>
                    )}
                  </div>
                </div>
              )}

              <div className="border-t pt-4 mt-4">
                <div className="text-sm text-gray-500">
                  <div className="mb-1">Добавлено: {new Date(property.created_at).toLocaleDateString('ru-RU')}</div>
                  <div>Обновлено: {new Date(property.updated_at).toLocaleDateString('ru-RU')}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PropertyDetail;
